import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Music, Package, LogIn } from "lucide-react";
import { api, coverUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "");

export default function Profile() {
  const { user, loading } = useAuth();
  const login = () => { const redirectUrl = window.location.origin + "/perfil"; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`; };
  const [lib, setLib] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/me/library").then((r) => setLib(r.data)).catch(() => setLib({ purchases: [], downloads: [] }));
  }, [user]);

  if (!loading && !user) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="profile-login-required">
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mb-4">Mi música</h1>
        <p className="text-white/60 mb-8">Inicia sesión con tu correo para ver todo lo que has comprado y descargado.</p>
        <button onClick={login} data-testid="profile-login-button" className="inline-flex items-center gap-2 btn-red text-white px-6 py-3 rounded-full font-semibold"><LogIn className="w-4 h-4" /> Iniciar sesión</button>
      </div>
    );
  }

  const linkFor = (x) => (x.item_type === "pack" || x.item_type === "pista" ? `/pack/${x.item_type === "pista" ? x.pack_id : x.item_id}` : `/track/${x.item_id}`);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="profile-page">
      <div className="flex items-center gap-5 mb-12">
        {user?.picture && <img src={user.picture} alt="" className="w-16 h-16 rounded-full border border-white/10" />}
        <div>
          <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-1">Mi música</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl leading-none" data-testid="profile-name">{user?.name}</h1>
          <div className="text-white/50 text-sm" data-testid="profile-email">{user?.email}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <section data-testid="profile-purchases">
          <h2 className="font-display font-bold text-lg mb-1">Compras</h2>
          <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mb-4">{lib ? `${lib.purchases.length} pagadas` : "Cargando…"}</div>
          <div className="space-y-2">
            {lib?.purchases.length === 0 && <div className="text-sm text-white/40 py-6 border border-dashed border-white/10 rounded-xl text-center">Aún no has comprado nada.</div>}
            {lib?.purchases.map((o) => (
              <div key={o.id} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-[#111116]" data-testid={`purchase-${o.id}`}>
                <div className="w-11 h-11 rounded overflow-hidden bg-neutral-900 shrink-0 flex items-center justify-center">
                  {o.cover_path ? <img src={coverUrl(o.cover_path)} alt="" className="w-full h-full object-cover" /> : o.item_type === "pack" ? <Package className="w-4 h-4 text-red-500" /> : <Music className="w-4 h-4 text-red-500" />}
                </div>
                <Link to={linkFor(o)} className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{o.item_title}</div>
                  <div className="text-xs text-white/40">{o.item_type === "pack" ? "Pack" : "Pista"} · €{Number(o.amount).toFixed(2)} · {fmtDate(o.paid_at || o.created_at)}</div>
                </Link>
                <a href={`${API}/download/bizum/${o.id}`} data-testid={`purchase-download-${o.id}`} className="p-2 rounded-lg text-white/60 hover:text-red-500 hover:bg-red-600/10" title="Descargar de nuevo"><Download className="w-4 h-4" /></a>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="profile-downloads">
          <h2 className="font-display font-bold text-lg mb-1">Descargas</h2>
          <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mb-4">{lib ? `${lib.downloads.length} archivos` : "Cargando…"}</div>
          <div className="space-y-2">
            {lib?.downloads.length === 0 && <div className="text-sm text-white/40 py-6 border border-dashed border-white/10 rounded-xl text-center">Todavía no has descargado nada con tu sesión iniciada.</div>}
            {lib?.downloads.map((d) => (
              <Link key={d.id} to={linkFor(d)} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-[#111116] hover:border-red-900/40" data-testid={`download-${d.id}`}>
                <div className="w-11 h-11 rounded overflow-hidden bg-neutral-900 shrink-0 flex items-center justify-center">
                  {d.cover_path ? <img src={coverUrl(d.cover_path)} alt="" className="w-full h-full object-cover" /> : <Music className="w-4 h-4 text-red-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{d.title}</div>
                  <div className="text-xs text-white/40">{{ pack: "Pack completo", pista: "Pista de pack", track: "Pista" }[d.item_type] || d.item_type} · {fmtDate(d.at)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
